import { formatSearchParams } from '#client/utils/formatString';

import { WEBINAR_LOCALIZED_TEXT, WEBINAR_SEARCH_PARAM } from './constants';

/**
 * Generates properties for displaying a webinar card.
 * @param {object} webinar - Webinar data with titleKey, descriptionKey, imagePath, mobileImagePath, and optional externalUrl
 * @param {Function} fm - Localization function
 * @returns {object} Formatted webinar properties with title, description, images, URLs, and actionLabel
 */
export function getWebinarProps(webinar, fm) {
  const {
    titleKey,
    descriptionKey,
    imagePath,
    mobileImagePath
  } = webinar;

  const title = fm({ id: titleKey });

  return {
    title,
    description: fm({ id: descriptionKey }),
    imagePath,
    mobileImagePath,
    actionLabel: fm({ id: WEBINAR_LOCALIZED_TEXT.actions.watchNow }),
    internalUrl: `?${WEBINAR_SEARCH_PARAM}=${formatSearchParams(title)}`
  };
}

/**
 * Generates properties for displaying a registration card.
 * @param {object} webinar - Webinar data with titleKey, descriptionKey, imagePath, mobileImagePath, and externalUrl
 * @param {Function} fm - Localization function
 * @returns {object} Formatted registration card properties with title, description, images, URL, and actionLabel
 */
export function getRegistrationProps(webinar, fm) {
  const {
    titleKey,
    descriptionKey,
    imagePath,
    mobileImagePath,
    externalUrl
  } = webinar;

  return {
    title: fm({ id: titleKey }),
    description: fm({ id: descriptionKey }),
    imagePath,
    mobileImagePath,
    actionLabel: fm({ id: WEBINAR_LOCALIZED_TEXT.actions.registerNow }),
    externalUrl
  };
}

/**
 * Generates properties for displaying a Q&A card.
 * @param {object} qa - Q&A data with titleKey, scheduleKey and optional externalUrl
 * @param {Function} fm - Localization function
 * @returns {object} Formatted Q&A properties with title, schedule, URL and actionLabel
 */
export function getQAProps(qa, fm) {
  const {
    titleKey,
    scheduleKey,
    externalUrl
  } = qa;

  return {
    title: fm({ id: titleKey }),
    schedule: fm({ id: scheduleKey }),
    actionLabel: fm({ id: WEBINAR_LOCALIZED_TEXT.actions.viewSchedule }),
    externalUrl
  };
}

/**
 * Classifies a list of webinars into promo banner, blueprints (featured), and more webinars (non-featured).
 *
 * @param {Array<object>} webinars - The array of webinar objects to classify.
 * @returns {object} An object containing:
 *   - {Array<Object>} blueprints: Array of webinars where `isFeatured` is true.
 *   - {Array<Object>} moreWebinars: Array of webinars where `isFeatured` is false.
 */
export function classifyWebinar(webinars = []) {
  const blueprints = [];
  const moreWebinars = [];

  for (const webinar of webinars) {
    (webinar.isFeatured ? blueprints : moreWebinars).push(webinar);
  }

  return { blueprints, moreWebinars };
}
